import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Send, Mic, MicOff, Bot, User,
    Paperclip, FileText
} from 'lucide-react';
import SpeechRecognition, { useSpeechRecognition } from 'react-speech-recognition';
import 'regenerator-runtime/runtime';
import { findBestMatch } from '../utils/nlp';
import faqsData from '../assets/faqs.json';
import { AI_API_BASE_URL } from '../utils/api';

// ─────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────
interface Message {
    id: string;
    text: string;
    sender: 'user' | 'bot';
    timestamp: Date;
    link?: string;
    linkText?: string;
}

// ─────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────
const SupportChat: React.FC = () => {
    const navigate = useNavigate();

    const [messages, setMessages] = useState<Message[]>([{
        id: 'welcome',
        text: "Hi! 👋 I'm the devNectar support team. Ask me anything about our team, services, or how to get started!",
        sender: 'bot',
        timestamp: new Date()
    }]);

    const [inputValue, setInputValue]   = useState('');
    const [isTyping, setIsTyping]       = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [suggestedOptions]            = useState<string[]>([
        'Services', 'Pricing', 'Portfolio', 'Contact', 'Technologies', 'Team', 'CEO', 'Support', 'Office Location'
    ]);

    const messagesEndRef = useRef<HTMLDivElement>(null);
    const fileInputRef   = useRef<HTMLInputElement>(null);

    const { transcript, listening, resetTranscript, browserSupportsSpeechRecognition } = useSpeechRecognition();


    useEffect(() => {
        if (transcript) setInputValue(transcript);
    }, [transcript]);

    const scrollToBottom = () => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    useEffect(() => { scrollToBottom(); }, [messages, isTyping, transcript]);

    // ── Core send ──────────────────────────────────────────
    const handleSend = useCallback(async (text: string) => {
        const trimmed = text.trim();
        if (!trimmed) return;

        setMessages(prev => [...prev, {
            id: Date.now().toString(),
            text: trimmed,
            sender: 'user',
            timestamp: new Date()
        }]);
        setInputValue('');
        resetTranscript();
        setIsTyping(true);
        const delay = Math.random() * 600 + 400;

        try {
            const res = await fetch(`${AI_API_BASE_URL}/chatbot/query`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ question: trimmed })
            });
            if (!res.ok) throw new Error();
            const data = await res.json();

            setTimeout(() => {
                setMessages(prev => [...prev, {
                    id: (Date.now() + 1).toString(),
                    text: data.answer,
                    sender: 'bot',
                    timestamp: new Date(),
                    link: data.redirect || undefined,
                    linkText: data.linkText || undefined
                }]);
                setIsTyping(false);
            }, delay);
        } catch {
            setTimeout(() => {
                const local = findBestMatch(trimmed, faqsData as any[]);
                setMessages(prev => [...prev, {
                    id: (Date.now() + 1).toString(),
                    text: local
                        ? local.answer
                        : "Sorry, I don't have this in my knowledge base. Please contact our team for further assistance.",
                    sender: 'bot',
                    timestamp: new Date()
                }]);
                setIsTyping(false);
            }, delay + 300);
        }
    }, [resetTranscript]);

    // Auto-send on voice pause
    useEffect(() => {
        if (!listening && transcript.trim().length > 0) {
            const t = setTimeout(() => {
                if (transcript.trim()) { handleSend(transcript); }
            }, 500);
            return () => clearTimeout(t);
        }
    }, [listening, transcript, handleSend]);

    const toggleListening = () => {
        if (listening) {
            SpeechRecognition.stopListening();
        } else {
            setInputValue('');
            resetTranscript();
            SpeechRecognition.startListening({ continuous: false, language: 'en-US' });
        }
    };

    // ── File upload ────────────────────────────────────────
    const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setIsUploading(true);
        setMessages(prev => [...prev, {
            id: `file-${Date.now()}`, text: `📎 ${file.name}`, sender: 'user', timestamp: new Date()
        }]);
        setIsTyping(true);
        const formData = new FormData();
        formData.append('file', file);
        try {
            const res = await fetch(`${AI_API_BASE_URL}/chatbot/upload`, { method: 'POST', body: formData });
            if (!res.ok) {
                const errData = await res.json().catch(() => ({}));
                throw new Error(errData.error || `Server returned ${res.status}`);
            }
            const data = await res.json();
            const keywords: string[] = data.keywords || [];
            const kwDisplay = keywords.length > 0 ? keywords.join(', ') : 'none found';
            setMessages(prev => [...prev, {
                id: `kw-${Date.now()}`,
                text: `I've processed **${file.name}**. Extracted keywords: **${kwDisplay}**.`,
                sender: 'bot', timestamp: new Date()
            }]);
            if (keywords.length > 0) {
                await new Promise(r => setTimeout(r, 700));
                const qRes = await fetch(`${AI_API_BASE_URL}/chatbot/query`, {
                    method: 'POST', headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ question: keywords[0], skipDocSearch: true })
                });
                if (qRes.ok) {
                    const qData = await qRes.json();
                    if (qData.answer) setMessages(prev => [...prev, {
                        id: `ans-${Date.now()}`, text: qData.answer, sender: 'bot',
                        timestamp: new Date(),
                        link: qData.redirect || undefined,
                        linkText: qData.linkText || undefined
                    }]);
                }
            }
        } catch (error: any) {
            const errorMsg = error.message || "Unknown error";
            setMessages(prev => [...prev, {
                id: Date.now().toString(),
                text: `Error: ${errorMsg}. Please try again with a valid file.`,
                sender: 'bot', timestamp: new Date()
            }]);
            console.error("Upload error:", error);
        } finally {
            setIsUploading(false); setIsTyping(false);
            if (fileInputRef.current) fileInputRef.current.value = '';
        }
    };

    const clearChat = () => {
        setMessages([{ id: 'welcome', text: "Chat cleared. How can I help you now?", sender: 'bot', timestamp: new Date() }]);
    };

    // ─────────────────────────────────────────────────────────
    // RENDER
    // ─────────────────────────────────────────────────────────
    return (
        <div className="flex flex-col h-screen bg-gray-50 font-sans overflow-hidden">

            {/* ── Header — matches Chatbot exactly ─────────────────── */}
            <div className="bg-blue-700 p-4 flex items-center justify-between select-none flex-shrink-0">
                <div className="flex items-center gap-3">
                    <button
                        onClick={() => navigate(-1)}
                        className="p-1 hover:bg-white/10 rounded-md transition-colors mr-1"
                        title="Go back"
                    >
                        <svg className="text-white w-[18px] h-[18px]" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                        </svg>
                    </button>
                    <div className="p-2 bg-white/20 rounded-lg">
                        <Bot size={20} className="text-white" />
                    </div>
                    <div>
                        <h3 className="text-white font-bold text-sm leading-tight">devNectar Support Team</h3>
                        <div className="flex items-center gap-2 mt-0.5">
                            <div className={`w-1.5 h-1.5 rounded-full shadow-[0_0_5px_rgba(74,222,128,0.6)] ${listening ? 'bg-red-400 animate-pulse' : (isTyping ? 'bg-amber-400' : 'bg-green-400')}`}></div>
                            <p className="text-blue-100/80 text-[10px] font-semibold tracking-wider uppercase">
                                {listening ? 'Listening' : (isTyping ? 'Thinking' : 'Online')}
                            </p>
                        </div>
                    </div>
                </div>
                {messages.length > 1 && (
                    <button 
                        onClick={clearChat}
                        className="text-[10px] bg-white/10 hover:bg-white/20 text-white px-3 py-1.5 rounded-lg border border-white/20 transition-all font-semibold uppercase tracking-wider"
                    >
                        Clear Chat
                    </button>
                )}
            </div>

            {/* ── Messages area — matches Chatbot exactly ───────────── */}
            <div className="flex-1 bg-gray-50 p-4 overflow-y-auto flex flex-col gap-4">
                {messages.map((msg) => (
                    <div key={msg.id} className={`flex gap-2 max-w-[85%] ${msg.sender === 'user' ? 'self-end flex-row-reverse' : ''}`}>
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${msg.sender === 'bot' ? 'bg-blue-100' : 'bg-gray-200'}`}>
                            {msg.sender === 'bot'
                                ? <Bot size={16} className="text-blue-600" />
                                : <User size={16} className="text-gray-600" />
                            }
                        </div>
                        <div className={`p-3 rounded-2xl shadow-sm border ${msg.sender === 'bot'
                            ? 'bg-white rounded-tl-none border-gray-100 text-gray-700'
                            : 'bg-blue-700 rounded-tr-none border-blue-600 text-white'
                        }`}>
                            {/* Markdown renderer */}
                            <div className="text-sm leading-relaxed space-y-1">
                                {msg.text.split('\n').map((line, i) => {
                                    if (line.startsWith('> ')) {
                                        return (
                                            <blockquote key={i} className="border-l-2 border-blue-300 pl-2 text-gray-500 italic text-xs">
                                                {line.slice(2).replace(/\*\*(.*?)\*\*/g, '$1')}
                                            </blockquote>
                                        );
                                    }
                                    const parts = line.split(/\*\*(.*?)\*\*/g);
                                    return (
                                        <p key={i}>
                                            {parts.map((part, j) =>
                                                j % 2 === 1
                                                    ? <strong key={j} className="font-semibold text-gray-800">{part}</strong>
                                                    : <span key={j}>{part}</span>
                                            )}
                                        </p>
                                    );
                                })}
                            </div>

                            {/* Action link button */}
                            {msg.link && (
                                <button
                                    onClick={() => {
                                        if (msg.link?.startsWith('http')) window.open(msg.link, '_blank');
                                        else navigate(msg.link!);
                                    }}
                                    className="mt-3 w-full py-2 px-4 bg-blue-50 hover:bg-blue-100 text-blue-700 text-xs font-semibold rounded-lg transition-colors flex items-center justify-center gap-2 border border-blue-100 group/btn"
                                >
                                    <span className="flex-1 truncate text-center">{msg.linkText || 'Explore'}</span>
                                    {msg.link?.startsWith('http') ? (
                                        <div className="p-1 bg-blue-600 rounded-md group-hover/btn:bg-blue-700 transition-colors">
                                            <FileText size={12} className="text-white" />
                                        </div>
                                    ) : (
                                        <Send size={12} className="rotate-45" />
                                    )}
                                </button>
                            )}

                            <span className={`text-[10px] mt-1 block ${msg.sender === 'bot' ? 'text-gray-400' : 'text-blue-200 text-right'}`}>
                                {msg.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                            </span>
                        </div>
                    </div>
                ))}

                {/* Typing indicator */}
                {isTyping && (
                    <div className="flex gap-2 max-w-[85%]">
                        <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                            <Bot size={16} className="text-blue-600" />
                        </div>
                        <div className="bg-white p-3 rounded-2xl rounded-tl-none shadow-sm border border-gray-100 flex gap-1 items-center">
                            <div className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce"></div>
                            <div className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce [animation-delay:0.2s]"></div>
                            <div className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce [animation-delay:0.4s]"></div>
                        </div>
                    </div>
                )}

                {/* Live voice transcript */}
                {listening && transcript && (
                    <div className="flex gap-2 max-w-[85%] self-end flex-row-reverse opacity-60">
                        <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center flex-shrink-0">
                            <Mic size={14} className="text-blue-500" />
                        </div>
                        <div className="bg-blue-100 p-3 rounded-2xl rounded-tr-none border border-blue-200 text-blue-700 text-sm italic">
                            {transcript}...
                        </div>
                    </div>
                )}

                <div ref={messagesEndRef} />
            </div>

            {/* ── Quick Options — matches Chatbot exactly ───────────── */}
            <div className="px-4 py-3 flex gap-2 overflow-x-auto bg-white border-t border-gray-50 scrollbar-custom flex-shrink-0">
                {suggestedOptions.map(opt => (
                    <button
                        key={opt}
                        onClick={() => {
                            if (opt === 'Contact')      handleSend('How do I start a project with devNectar?');
                            else if (opt === 'Services') handleSend('What services do you provide?');
                            else if (opt === 'Portfolio') handleSend('Can I see your portfolio of projects?');
                            else if (opt === 'Team')    handleSend('How many employees are in the devNectar team?');
                            else if (opt === 'Pricing') handleSend('What is your pricing model?');
                            else if (opt === 'Technologies') handleSend('What technologies do you work with?');
                            else handleSend(opt);
                        }}
                        className="whitespace-nowrap px-3 py-1 bg-blue-50/50 hover:bg-blue-100/80 text-blue-700 text-xs font-medium rounded-full border border-blue-100/50 transition-all active:scale-95"
                    >
                        {opt}
                    </button>
                ))}
            </div>

            {/* ── Input Area — matches Chatbot exactly ─────────────── */}
            <form
                className="p-4 bg-white border-t border-gray-100 flex-shrink-0"
                onSubmit={(e) => { e.preventDefault(); handleSend(inputValue); }}
            >
                <div className="relative flex items-center gap-2">
                    <input
                        type="file"
                        ref={fileInputRef}
                        className="hidden"
                        accept=".pdf,.docx,.png,.jpg,.jpeg"
                        onChange={handleFileUpload}
                    />
                    <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={isUploading}
                        className="flex-shrink-0 p-2.5 text-gray-400 hover:bg-blue-50 hover:text-blue-600 rounded-xl transition-all"
                    >
                        <Paperclip size={18} className={isUploading ? 'animate-pulse text-blue-500' : ''} />
                    </button>

                    <input
                        type="text"
                        value={inputValue}
                        onChange={(e) => setInputValue(e.target.value)}
                        placeholder={listening ? 'Listening… speak now 🎤' : (isUploading ? 'Processing file...' : 'Type your message...')}
                        className={`flex-1 pl-4 pr-4 py-3 bg-gray-50 border rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none text-sm transition-all
                            ${listening
                                ? 'border-blue-400 bg-blue-50 placeholder-blue-400 ring-2 ring-blue-300'
                                : 'border-gray-200'
                            }
                        `}
                    />

                    {browserSupportsSpeechRecognition && (
                        <button
                            type="button"
                            onClick={toggleListening}
                            title={listening ? 'Stop listening' : 'Speak your question'}
                            className={`flex-shrink-0 p-2.5 rounded-xl transition-all duration-200
                                ${listening
                                    ? 'bg-blue-500 text-white animate-mic-pulse shadow-blue-300 shadow-lg'
                                    : 'bg-gray-100 text-gray-500 hover:bg-blue-100 hover:text-blue-600'
                                }
                            `}
                        >
                            {listening ? <MicOff size={16} /> : <Mic size={16} />}
                        </button>
                    )}

                    <button
                        type="submit"
                        className="flex-shrink-0 p-2.5 bg-blue-700 text-white rounded-xl hover:bg-blue-800 transition-colors"
                    >
                        <Send size={16} />
                    </button>
                </div>

                {listening && (
                    <p className="text-[10px] text-blue-500 mt-1.5 text-center animate-pulse">
                        🎤 Listening... speak clearly and pause when done
                    </p>
                )}
                {!browserSupportsSpeechRecognition && (
                    <p className="text-[10px] text-gray-400 mt-1 text-center">
                        Voice input not supported in this browser
                    </p>
                )}
                <p className="text-[11px] text-gray-400 font-medium text-center mt-2 tracking-wide select-none">
                    Powered by{' '}
                    <a
                        href="https://devnectar.in"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="font-semibold text-blue-600 hover:text-blue-700 hover:underline cursor-pointer transition-colors"
                    >
                        devNectar
                    </a>
                </p>
            </form>
        </div>
    );
};

export default SupportChat;
